import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  profileRequestResponseSchema,
  profileRequestStatusSchema,
  profileRequestsResponseSchema,
  type ProfileRequest,
  type ProfileRequestStatus,
} from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import { ApiError } from '../../components/ApiError.js';
import { formatDate } from '../issuance/FormsPage.js';
import { PatchSummary } from './MyFilePage.js';

/** The approvers' queue (hr / admin): each request, its changes, approve or reject with a reason. */
export function ProfileRequestsPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ProfileRequestStatus>('pending');
  const list = useApi(`/profile-requests?status=${status}`, profileRequestsResponseSchema);

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="profile-requests">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{t('profileRequests.title')}</h2>
        <label className="text-sm text-slate-600">
          {t('profileRequests.filter')}{' '}
          <select
            value={status}
            onChange={(e) => setStatus(profileRequestStatusSchema.parse(e.target.value))}
            data-testid="profile-requests-status"
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm text-slate-900"
          >
            {profileRequestStatusSchema.options.map((s) => (
              <option key={s} value={s}>
                {t(`profileRequests.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-1 text-sm text-slate-500">{t('profileRequests.hint')}</p>

      {list.kind === 'loading' && <p className="mt-3 text-slate-500">{t('home.loading')}</p>}
      {list.kind === 'error' && <ApiError code={list.code} requestId={list.requestId} />}
      {list.kind === 'ready' && list.data.requests.length === 0 && (
        <p className="mt-3 text-sm text-slate-500" data-testid="profile-requests-empty">
          {t('profileRequests.empty')}
        </p>
      )}
      {list.kind === 'ready' && (
        <ul className="mt-3 divide-y divide-slate-100">
          {list.data.requests.map((r) => (
            <RequestCard key={r.id} request={r} onDecided={list.reload} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RequestCard({ request, onDecided }: { request: ProfileRequest; onDecided: () => void }) {
  const { t, i18n } = useTranslation();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const approve = useAction(() => apiPost(`/profile-requests/${request.id}/approve`, profileRequestResponseSchema, {}));
  const reject = useAction(() => apiPost(`/profile-requests/${request.id}/reject`, profileRequestResponseSchema, { reason }));
  const busy = approve.busy || reject.busy;

  return (
    <li className="py-3" data-testid="profile-request" data-id={request.id} data-status={request.status}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-slate-900">
          {request.teamId ? (
            <Link to={`/org/${request.teamId ?? request.platoonId}/soldier/${request.uid}`} className="hover:underline">
              {request.soldierName}
            </Link>
          ) : (
            request.soldierName
          )}{' '}
          <span className="latin-inline text-sm text-slate-500">{request.personalNumber}</span>
        </span>
        <span className="latin-inline text-sm text-slate-500">
          {t('profileRequests.requestedAt')} {formatDate(request.requestedAt, i18n.language)}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">{t('profileRequests.changes')}</p>
      <PatchSummary patch={request.patch} />
      {request.note && (
        <p className="mt-1 text-sm text-slate-700">
          {t('profileRequests.note')}: {request.note}
        </p>
      )}
      {request.status !== 'pending' && (
        <p className="mt-1 text-sm text-slate-600">
          {t(`profileRequests.status.${request.status}`)}
          {request.decidedByName && ` · ${t('profileRequests.decidedBy')} ${request.decidedByName}`}
          {request.reason && ` · ${request.reason}`}
        </p>
      )}
      {request.status === 'pending' && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {!rejecting && (
            <>
              <button
                type="button"
                onClick={() => void approve.run().then((r) => r && onDecided())}
                disabled={busy}
                data-testid="request-approve"
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
              >
                {t('profileRequests.approve')}
              </button>
              <button
                type="button"
                onClick={() => setRejecting(true)}
                disabled={busy}
                data-testid="request-reject-open"
                className="rounded-lg px-3 py-1.5 text-sm text-red-700 ring-1 ring-red-300 transition hover:bg-red-50"
              >
                {t('profileRequests.reject')}
              </button>
            </>
          )}
          {rejecting && (
            <>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('profileRequests.rejectReason')}
                data-testid="request-reject-reason"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900"
              />
              <button
                type="button"
                onClick={() => void reject.run().then((r) => r && onDecided())}
                disabled={busy || reason.trim().length < 3}
                data-testid="request-reject-submit"
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-40"
              >
                {t('profileRequests.rejectSubmit')}
              </button>
              <button type="button" onClick={() => setRejecting(false)} className="text-sm text-slate-600 hover:underline">
                {t('users.cancel')}
              </button>
            </>
          )}
        </div>
      )}
      <ActionError error={approve.error ?? reject.error} />
    </li>
  );
}
